import React, { useState } from 'react';
import Modal from 'react-modal';
import moment from 'moment-timezone';
import { getDay } from 'date-fns';
import { DocumentNode } from 'graphql';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Container from 'components/Container';
import Divider from 'components/Divider';
import Grid from 'components/Grid';
import Icon from 'components/Icon';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';

import SaveSingleDayOverrideDialog from 'coaching/components/CoachAvailabilityView/SaveSingleDayOverrideDialog';
import { SingleDayCoachAvailability } from 'coaching/containers/CoachAvailabilityContainer';
import { getWeekdayForWeekOffset } from 'coaching/lib/timeIntervals';
import { WeekTraversalHeader } from 'coaching/components/CoachingHomeView/BookOneOffSession';

import '../styles/CoachAvailabilitiesByDayList.scss';

export type Props = {
  availabilities: SingleDayCoachAvailability[];
  weekOffset: number;
  weekStartDate: string;
  weekEndDate: string;
  onWeekForward: () => void;
  onWeekBack: () => void;
  timezone: string;
  availabilityQuery?: { query: DocumentNode; variables?: Object };
};

export default function CoachAvailabilitiesByDayList({
  onWeekForward,
  onWeekBack,
  weekOffset,
  weekStartDate,
  weekEndDate,
  availabilities,
  timezone,
  availabilityQuery,
}: Props) {
  const [singleDayAvailabilityToEdit, setSingleDayAvailabilityToEdit] = useState<
    SingleDayCoachAvailability | undefined
  >(undefined);

  return (
    <>
      <Modal isOpen={Boolean(singleDayAvailabilityToEdit)}>
        <SaveSingleDayOverrideDialog
          singleDayAvailability={singleDayAvailabilityToEdit}
          timezone={timezone}
          onRequestHide={() => setSingleDayAvailabilityToEdit(undefined)}
          availabilityQuery={availabilityQuery}
        />
      </Modal>
      <Container flush size="small" inline>
        <Box
          inline
          noShadow
          textAlign="left"
          className="CoachAvailabilitiesByDayList-box"
          bandHeader={
            <WeekTraversalHeader
              onWeekBack={onWeekBack}
              onWeekForward={onWeekForward}
              startDateTimeRange={weekStartDate}
              endDateTimeRange={weekEndDate}
              weekOffset={weekOffset}
              timeRangeTextSize="small"
              size="xxxsmall"
            />
          }
          bandHeaderSize="xsmall">
          <BoxBody size="xsmall">
            {availabilities.map((availability, index) => (
              <Row size="xxxxsmall" key={`${index}`}>
                <SingleDayCoachAvailabilityRow
                  weekOffset={weekOffset}
                  availability={availability}
                  shouldShowDivider={index !== availabilities.length - 1}
                  onClick={setSingleDayAvailabilityToEdit}
                />
              </Row>
            ))}
          </BoxBody>
        </Box>
      </Container>
    </>
  );
}

function SingleDayCoachAvailabilityRow(props: {
  weekOffset: number;
  availability: SingleDayCoachAvailability;
  onClick: (availability: SingleDayCoachAvailability) => void;
  shouldShowDivider?: boolean;
}) {
  const { availability, shouldShowDivider, onClick } = props;
  const { availableTimeframes } = availability;

  const date = availability.date;
  const dayOfWeekIndex = getDay(date);
  const isUnavailable = availableTimeframes.length === 0;

  return (
    <a onClick={() => onClick(availability)}>
      <Grid columnGap="xxxxxsmall" columns={2} className="CoachAvailabilitiesByDayList-dayRow">
        <Text weight="semibold" lineHeight="large">
          {getWeekdayForWeekOffset(dayOfWeekIndex, props.weekOffset).format('ddd, MMM D')}
        </Text>
        <MediaObject figure="right" center key={date}>
          <div
            className="CoachAvailabilitiesByDayList-dayRow-timeframes"
            key={`${availability.date}`}>
            {isUnavailable ? (
              <Text>Not available</Text>
            ) : (
              availableTimeframes.map(timeframe => {
                return (
                  <div key={`${availability.date}-${timeframe.startTime}`}>
                    <Text>
                      {moment(timeframe.startTime, 'HH:mm').format('h:mm A')}
                      {' - '}
                    </Text>
                    <Text>{moment(timeframe.endTime, 'HH:mm').format('h:mm A')}</Text>
                  </div>
                );
              })
            )}
            {availability.override && (
              <Text
                key={`${availability.date}-${availability.override.label}`}
                element="div"
                size="xxxsmall"
                color="graphite">
                Edited
              </Text>
            )}
          </div>
          <div>
            <Icon name="pencil-stroke" className="CoachAvailabilitiesByDayList-dayRow-editIcon" />
          </div>
        </MediaObject>
      </Grid>
      {shouldShowDivider && <Divider size="xxsmall" />}
    </a>
  );
}
